import { Box, Group, Stack } from '@mantine/core';
import { IAutomation } from 'pages/revisions-module/template-editor/editors/pipeline/types';
import { IconArrowRight } from '@tabler/icons-react';
import RevisionStatusIcon from 'components/RevisionStatusIcon';
import P3Medium from 'components/typography/P3Medium';
import AutomationRow from 'pages/revisions-module/template-editor/editors/pipeline/AutomationRow';
import { Fragment, useCallback, useState } from 'react';
import findElementParent from 'utils/find-element-parent';
import { usePipelineData } from 'pages/revisions-module/template-editor/editors/pipeline/PipelineDataProvider';

/**
 * Groups automations by source and target status.
 */
export default function AutomationGroup({ automations }: { automations: IAutomation[] }) {
  const { moveAutomationAfter, moveAutomationToStart } = usePipelineData();

  const [draggingIndex, setDraggingIndex] = useState<number | null>(null);
  const [hoveredIndex, setHoveredIndex] = useState<number | null>(null);

  const onDragOver = useCallback(
    (e: React.DragEvent) => {
      const hoverRow = findElementParent(e.target as Element, (el) => el.classList.contains('group/automation-row'));

      if (draggingIndex !== null && hoverRow !== null) {
        e.preventDefault();
        const isAboveHalf = e.clientY - hoverRow.getBoundingClientRect().top < hoverRow.clientHeight / 2;
        const index = Number(hoverRow.getAttribute('data-index')) + (isAboveHalf ? 0 : 1);
        setHoveredIndex(index);
      }
    },
    [draggingIndex]
  );

  const onDragEnd = useCallback(() => {
    setDraggingIndex(null);
    setHoveredIndex(null);
  }, []);

  const onDrop = useCallback(
    (e: React.DragEvent) => {
      e.preventDefault();

      if (hoveredIndex !== null && draggingIndex !== null && hoveredIndex !== draggingIndex) {
        const slot = hoveredIndex - 1;
        const draggingAutomation = automations[draggingIndex];
        const afterAutomation = automations[slot];

        if (slot === -1) {
          moveAutomationToStart(draggingAutomation.id);
        } else if (draggingAutomation.id !== afterAutomation.id) {
          moveAutomationAfter(draggingAutomation.id, afterAutomation.id);
        } else {
          // No change
        }
      }

      setDraggingIndex(null);
      setHoveredIndex(null);
    },
    [automations, draggingIndex, hoveredIndex, moveAutomationAfter, moveAutomationToStart]
  );

  const statusSource = automations[0].statusSource;
  const statusTarget = automations[0].statusTarget;

  if (automations.length === 0) {
    /* istanbul ignore next -- explicitly authorized unreachable coverage blocker */
    return null;
  }

  return (
    <Stack spacing={8}>
      <Group spacing={8} align="center">
        <RevisionStatusIcon statusId={statusSource} />
        <IconArrowRight />
        <RevisionStatusIcon statusId={statusTarget} />
        <P3Medium>({automations.length})</P3Medium>
      </Group>

      <Stack spacing={0} onDragOver={onDragOver} onDrop={onDrop}>
        {automations.map((automation, index) => (
          <Fragment key={automation.id}>
            {hoveredIndex === index && <Box h={2} bg="red.5" />}
            <AutomationRow
              data={automation}
              index={index}
              dropMode={draggingIndex !== null}
              dragMode={draggingIndex === index}
              onDragStart={() => setDraggingIndex(index)}
              onDragEnd={onDragEnd}
            />
          </Fragment>
        ))}
        {hoveredIndex === automations.length && <Box h={2} bg="red.5" />}
      </Stack>
    </Stack>
  );
}
